import fairFigureLogo from '../assets/images/marketplace/fair-figure-logo.svg';
import navLogo from '../assets/images/marketplace/nav-logo.png';
import preferredFundingGroupLogo from '../assets/images/marketplace/preferred-funding-group-logo.png';
import wealthyResultsLogo from '../assets/images/marketplace/wealthy-results-logo.png';
import perfectClientUsaLogo from '../assets/images/marketplace/perfect-client-usa-logo.png';
import { ExternalUrl } from '../marketplace/types';
import { FundingDto } from './fundingApi';

export type FundingData = FundingDto;

export const CREDIT_TYPE_CARD = 'card';
export const CREDIT_TYPE_TERM_LOAN = 'term-loan';
export type CreditType = typeof CREDIT_TYPE_CARD | typeof CREDIT_TYPE_TERM_LOAN;

export type FundingPartner = {
  code: string;
  name: string;
  logo?: string;
  pitch: string;
  descriptionMarkdown: string;
  creditType: CreditType;
  minLoanAmount?: number;
  maxLoanAmount?: number;
  rank?: number;
  requirements: {
    minPersonalCreditScore?: number;
    minMonthsInBusiness?: number;
    minMonthlyRevenue?: number;
    minAnnualPersonalIncome?: number;
    annualPersonalIncomeWindowYears?: number;
    maxDebtToIncomeRatio?: number;
  };
  domain: string;
  url: ExternalUrl | null;
};

export type FundingPartnerMatch = {
  isMatch: boolean | null;
  message: string;
};

export type FundingPartnerSearchResultMatch = {
  minPersonalCreditScore: FundingPartnerMatch;
  minMonthsInBusiness: FundingPartnerMatch;
  minMonthlyRevenue: FundingPartnerMatch;
  minAnnualPersonalIncome: FundingPartnerMatch;
  maxDebtToIncomeRatio: FundingPartnerMatch;
  loanAmount: FundingPartnerMatch;
};

export type FundingPartnerSearchResult = {
  fundingPartner: FundingPartner;
  match: FundingPartnerSearchResultMatch;
};

export const FUNDING_PARTNERS: FundingPartner[] = [
  {
    code: 'fairfigure__card',
    name: 'FairFigure',
    creditType: CREDIT_TYPE_CARD,
    logo: fairFigureLogo,
    pitch:
      'Fund your business with the FairFigure card - a true “EIN-Only” Business Credit Builder Card',
    descriptionMarkdown: `
Build your business credit AND get funded at the same time!
Use this card to pay your Growegy invoices and benefit from having two credit reports from a single transaction.
* No personal guarantees, paperwork, or FICO required.
* Funding is based on business revenue, not personal credit scores.`,
    maxLoanAmount: 20_000,
    rank: 1,
    requirements: {
      minMonthsInBusiness: 3,
      minMonthlyRevenue: 2_500,
    },
    domain: 'fairfigure.com',
    url: 'https://fairfigure.com/embed/application/growegy',
  },
  {
    code: 'nav__card',
    name: 'Nav',
    creditType: CREDIT_TYPE_CARD,
    logo: navLogo,
    pitch: 'Build business credit on this daily charge card without carrying a balance.',
    rank: 1,
    descriptionMarkdown: `
Set up autopay to repay your card on time, at the end of each day. Daily repayments make it easier to safely manage your money.
* No revenue minimums
* No time in business requirements
* No personal credit check
* No security deposit
* No personal guarantee`,
    maxLoanAmount: 20_000,
    requirements: {},
    domain: 'nav.com',
    url: 'https://nav.nkwcmr.net/c/5876351/2154908/2410',
  },
  {
    code: 'preferred_funding_group__card',
    name: 'Preferred Funding Group',
    creditType: CREDIT_TYPE_CARD,
    logo: preferredFundingGroupLogo,
    pitch:
      'Secure the funding you need to start or grow your business with help from our ' +
      "trusted team of Experts! Apply In 60 Seconds. It's fast, secure and easy.",
    descriptionMarkdown: `
* We understand every score is different so we will create an action plan to help you get pre-approved.
* 0% interest for 6-12 months
* Receive cards in 7-15 days
* No hidden fee-fair terms
* Over 100 million funded yearly
* 95% Approval rate`,
    rank: 3,
    minLoanAmount: 50_000,
    maxLoanAmount: 150_000,
    requirements: {
      minPersonalCreditScore: 700,
    },
    domain: 'preferredfundinggroup.com',
    url: 'https://preferredfundinggroup.wufoo.com/forms/z150r9vf1p20jku/',
  },
  {
    code: 'preferred_funding_group__loan',
    name: 'Preferred Funding Group',
    creditType: CREDIT_TYPE_TERM_LOAN,
    logo: preferredFundingGroupLogo,
    pitch:
      'Secure the funding you need to start or grow your business with help from our ' +
      "trusted team of Experts! Apply In 60 Seconds. It's fast, secure and easy.",
    descriptionMarkdown: `
* 9-15% APR (fully amortized)
* Funding in 7-10 business days
* Reports to personal credit`,
    rank: 3,
    minLoanAmount: 50_000,
    maxLoanAmount: 500_000,
    requirements: {
      minPersonalCreditScore: 680,
      minAnnualPersonalIncome: 50_000,
      annualPersonalIncomeWindowYears: 2,
      maxDebtToIncomeRatio: 40,
    },
    domain: 'preferredfundinggroup.com',
    url: 'https://preferredfundinggroup.wufoo.com/forms/z150r9vf1p20jku/',
  },
  {
    code: 'wealthy_results__loan',
    name: 'Wealthy Results',
    creditType: CREDIT_TYPE_TERM_LOAN,
    logo: wealthyResultsLogo,
    pitch: 'Unleash Your Potential & Discover Your Ideal Solution',
    descriptionMarkdown: `
* Our dedicated team specializes in securing funding that can make the difference. 
* We work closely with clients while leveraging our expertise to highlight the strategic utilization of funds. 
* Whether you're a new entrepreneur looking to bring your vision to life or an established business aiming for expansion, our consulting services are tailored to ensure that the funding you receive ensures your growth and achievement.`,
    rank: 4,
    requirements: {
      minPersonalCreditScore: 700,
    },
    domain: 'wealthy-results.com',
    url: null,
  },
  {
    code: 'perfect_client_usa__loan',
    name: 'Perfect Client USA',
    creditType: CREDIT_TYPE_TERM_LOAN,
    logo: perfectClientUsaLogo,
    pitch: 'We Help Businesses Pre-Qualify For Financing',
    descriptionMarkdown: `
 1. Our business scan shows you what you pre-qualify for now. 
 2. Next, our success system details the key steps you must take. 
 3. Then, we build your business credit scores to access more funding.`,
    requirements: {
      minPersonalCreditScore: 680,
    },
    domain: 'perfectclientusa.com',
    url: 'https://member.perfectclientusa.com/agent/growegy',
  },
  {
    code: 'houston_fintech__loan',
    name: 'Houston Fintech',
    creditType: CREDIT_TYPE_TERM_LOAN,
    pitch: 'Private Money Financing for Real Estate Investors',
    descriptionMarkdown: `
* Fix-and-flip loans
* New construction loans
* Cash-out refinance
* Purchase / bridge loans`,
    minLoanAmount: 20_000,
    maxLoanAmount: 50_000_000,
    requirements: {
      minPersonalCreditScore: 600,
    },
    domain: 'houstonfintech.com',
    url: null,
  },
];
